import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { SignaturePad } from '../../components/SignaturePad';
import type { CollectedSig } from './types';

export interface SignerFormProps {
  title: string;
  role: string;
  defaultName: string;
  prefillSig?: string | null;
  onSign: (payload: CollectedSig) => void;
  onCancelConfirmed?: () => void;
  disabled?: boolean;
}

export const SignerForm: React.FC<SignerFormProps> = ({
  title,
  role,
  defaultName,
  prefillSig,
  onSign,
  onCancelConfirmed,
  disabled,
}) => {
  const [name, setName] = useState(defaultName);
  const [sig, setSig] = useState<string | null>(prefillSig ?? null);
  const [agreed, setAgreed] = useState(false);
  const [confirmed, setConfirmed] = useState(!!prefillSig);

  const signedAtRef = useRef<string | null>(null);

  useEffect(() => {
    setName(defaultName);
  }, [defaultName]);

  useEffect(() => {
    if (prefillSig && !confirmed) {
      setSig(prefillSig);
      setAgreed(true);
      setConfirmed(true);

      const signedAt = new Date().toISOString();
      signedAtRef.current = signedAt;

      onSign({
        dataUrl: prefillSig,
        signerName: name || defaultName,
        signerRole: role,
        signedAt,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillSig]);

  /**
   * SignaturePad transmet simplement l'image de la signature.
   * Aucune validation définitive ici.
   */
  const handleSign = (dataUrl: string) => {
    setSig(dataUrl);
  };

  /**
   * Seule validation définitive de la signature.
   */
  const handleConfirm = () => {
    if (!sig || !name.trim() || !agreed) return;

    const signedAt = new Date().toISOString();

    signedAtRef.current = signedAt;
    setConfirmed(true);

    onSign({
      dataUrl: sig,
      signerName: name.trim(),
      signerRole: role,
      signedAt,
    });
  };

  const canConfirm =
    !!sig &&
    name.trim().length > 0 &&
    agreed &&
    !confirmed &&
    !disabled;

  return (
    <div
      className={`card border ${
        role === 'PRACTITIONER'
          ? 'border-blue-200'
          : role === 'LEGAL_GUARDIAN'
            ? 'border-amber-200'
            : 'border-gray-200'
      } space-y-4`}
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-sm">{title}</h3>

          {confirmed && signedAtRef.current && (
            <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Signé le{' '}
              {new Date(signedAtRef.current).toLocaleString('fr-FR')}
            </p>
          )}
        </div>

        {confirmed && (
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        )}
      </div>

      <p className="text-xs text-gray-500 -mt-2">
        La signature apparaît à la fin du PDF avec l&apos;horodatage une fois
        validée.
      </p>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Nom complet du signataire <span className="text-red-500">*</span>
        </label>

        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={confirmed || disabled}
          placeholder="Prénom Nom"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
        />
      </div>

      {!confirmed && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Votre signature <span className="text-red-500">*</span>
          </label>

          <SignaturePad
            onSign={handleSign}
            disabled={confirmed || disabled}
          />
        </div>
      )}

      {!confirmed && (
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={confirmed || disabled}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
          />

          <span className="text-xs text-gray-600 leading-relaxed">
            Je reconnais avoir lu le document ci-dessus dans son intégralité et
            j&apos;accepte de le signer électroniquement. Cette signature a la
            même valeur qu&apos;une signature manuscrite conformément à la
            législation en vigueur.
          </span>
        </label>
      )}

      {!confirmed && (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Valider la signature
        </button>
      )}

      {confirmed && role === 'CLIENT' && onCancelConfirmed && (
        <button
          type="button"
          onClick={onCancelConfirmed}
          disabled={disabled}
          className="w-full text-sm font-medium text-red-700 border border-red-200 rounded-xl py-2 hover:bg-red-50 disabled:opacity-50"
        >
          Annuler la signature du client
        </button>
      )}
    </div>
  );
};